import { Raycaster, Vector2, Vector3, Plane, Object3D } from "three";
import { GraphicService } from "./GraphicService";
import { Room } from "./Room";

export class AccessPointDragHandler {
    private raycaster = new Raycaster();
    private mouse = new Vector2();
    private dragPlane = new Plane();
    private dragging = false;
    private dragTarget: Object3D | null = null;
    private dragWall: string | null = null;
    private room: Room;
    private canvas: HTMLCanvasElement;

    constructor(room: Room, canvas: HTMLCanvasElement) {
        this.room = room;
        this.canvas = canvas;

        this.onPointerDown = this.onPointerDown.bind(this);
        this.onPointerMove = this.onPointerMove.bind(this);
        this.onPointerUp = this.onPointerUp.bind(this);

        canvas.addEventListener("pointerdown", this.onPointerDown);
        canvas.addEventListener("pointermove", this.onPointerMove);
        canvas.addEventListener("pointerup", this.onPointerUp);
    }

    private getMouseNDC(e: PointerEvent): void {
        const rect = this.canvas.getBoundingClientRect();

        this.mouse.x = ((e.clientX - rect.left) / rect.width) * 2 - 1;
        this.mouse.y = -((e.clientY - rect.top) / rect.height) * 2 + 1;
    }

    private onPointerDown(e: PointerEvent): void {
        if (!GraphicService.camera) {
            console.warn("AccessPointDragHandler: camera not ready");
            return;
        }

        this.getMouseNDC(e);
        this.raycaster.setFromCamera(this.mouse, GraphicService.camera);

        const meshes = this.room.accessPoints.flatMap((ap: any) => {
            return ap.meshes ?? [];
        });
        const hits = this.raycaster.intersectObjects(meshes, true);

        if (!hits.length) return;

        const hit = hits[0].object;

        if (!hit.userData.draggable) return;

        this.dragging = true;

        this.dragTarget = hit.userData.group;
        this.dragWall = hit.userData.wall;

        const dragTarget = this.dragTarget;

        if (!dragTarget) return;

        switch (this.dragWall) {
            case "north":
            case "south":
                this.dragPlane.setFromNormalAndCoplanarPoint(
                    new Vector3(0, 0, 1),
                    dragTarget.position
                );
                break;

            case "east":
            case "west":
                this.dragPlane.setFromNormalAndCoplanarPoint(
                    new Vector3(1, 0, 0),
                    dragTarget.position
                );
                break;
        }

        if (GraphicService.control) {
            GraphicService.control.enabled = false;
        }
    }

    private onPointerMove(e: PointerEvent): void {
        if (!this.dragging || !this.dragTarget || !this.dragWall) return;

        this.getMouseNDC(e);
        if (!GraphicService.camera) return;

        this.raycaster.setFromCamera(this.mouse, GraphicService.camera);

        const intersection = new Vector3();
        const didHitPlane = this.raycaster.ray.intersectPlane(this.dragPlane, intersection);

        if (!didHitPlane) return;

        const roomSize = this.room.currentConfiguration.roomSize;

        const doorwayHalfWidth = 0.7;
        const wallPadding = 0.25;

        const halfW = roomSize.width / 2 - doorwayHalfWidth - wallPadding;
        const halfL = roomSize.length / 2 - doorwayHalfWidth - wallPadding;

        switch (this.dragWall) {
            case "north":
                this.dragTarget.position.x = Math.max(-halfW, Math.min(halfW, intersection.x));
                this.dragTarget.position.z = -roomSize.length / 2 + 0.04;
                break;

            case "south":
                this.dragTarget.position.x = Math.max(-halfW, Math.min(halfW, intersection.x));
                this.dragTarget.position.z = roomSize.length / 2 - 0.04;
                break;

            case "east":
                this.dragTarget.position.x = roomSize.width / 2 - 0.04;
                this.dragTarget.position.z = Math.max(-halfL, Math.min(halfL, intersection.z));
                break;

            case "west":
                this.dragTarget.position.x = -roomSize.width / 2 + 0.04;
                this.dragTarget.position.z = Math.max(-halfL, Math.min(halfL, intersection.z));
                break;
        }

        this.dragTarget.updateMatrixWorld(true);
        this.syncToConfig();
    }

    private onPointerUp(): void {
        this.dragging = false;
        this.dragTarget = null;
        this.dragWall = null;

        if (GraphicService.control) {
            GraphicService.control.enabled = true;
        }
    }

    private syncToConfig(): void {
        if (!this.dragTarget) return;

        const id = this.dragTarget.userData.id;
        const wall = this.dragTarget.userData.wall;
        const config = this.room.currentConfiguration;

        if (!config.accessPoints) return;

        this.room.currentConfiguration = {
            ...config,
            accessPoints: config.accessPoints.map((a) => {
                if (a.id !== id) return a;

                return {
                    ...a,
                    offset:
                        wall === "north" || wall === "south"
                            ? this.dragTarget!.position.x
                            : this.dragTarget!.position.z,
                };
            }),
        };
    }

    dispose(): void {
        this.canvas.removeEventListener("pointerdown", this.onPointerDown);
        this.canvas.removeEventListener("pointermove", this.onPointerMove);
        this.canvas.removeEventListener("pointerup", this.onPointerUp);
    }
}
